/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { notifications } from '@mantine/notifications';
import ky, { HTTPError } from 'ky';

import { ApiPaths, isApiPath } from '@fai/constants/shared-urls';
import { envIs } from '@fai/friendli-suite/shared/environments/conditionals';
import { environment } from '@fai/friendli-suite/shared/environments/environment';
import { reportHTTPErrorToSentry } from '@fai/third-party/sentry';
import { errorProps } from '@fai/ui/components/notification/props';

const ApiPathsNotToReport: Set<ApiPaths> = new Set([ApiPaths.Login]);

const errorMessages: { [key in ApiPaths]?: string } = {
  [ApiPaths.Login]: 'Invalid Credentials',
  [ApiPaths.ConfirmLoginInvitation]: 'Failed to confirm invite',
  [ApiPaths.AcceptInvitation]: 'Failed to accept invitation',
  [ApiPaths.SendResetPasswordEmail]: 'Failed to resend email',
  [ApiPaths.ConfirmResetPassword]: 'Failed to update password',
  [ApiPaths.SignOut]: 'SignOut failed',
};

export const baseKy = ky.create({
  credentials: 'include',
  prefixUrl: environment.SERVER_URL,
  hooks: {
    afterResponse: [
      async (request, options, response) => {
        if (!response.ok) {
          const requestedApiPath = request.url.replace(`${environment.SERVER_URL}/`, '');

          let errorMessage = envIs.PRODUCTION ? 'Something went wrong...' : response.statusText;
          let errorToReport: HTTPError;

          if (isApiPath(requestedApiPath)) {
            errorMessage = errorMessages[requestedApiPath] as string;
            errorToReport = new HTTPError(response, request, options);
            errorToReport.message = errorMessage;
          } else {
            errorToReport = new HTTPError(response, request, options);
          }

          notifications.show({ ...errorProps, message: errorMessage });

          if (!isApiPath(requestedApiPath) || !ApiPathsNotToReport.has(requestedApiPath)) {
            reportHTTPErrorToSentry(errorToReport);
          }

          throw errorToReport;
        }
      },
    ],
  },
});

export const parseHTTPError = async <T>(error: unknown) => {
  if (error instanceof HTTPError) {
    const errorJson = await error.response.json();

    return errorJson as T;
  }

  throw new Error('Not a KY request cannot parse');
};
